import { PersonDto } from '../services/api';
import { getPersonDisplayName } from './relationshipLabels';
import { normalizeForSearch } from './unicode';

/**
 * Returns the IDs of all people whose display name contains the query
 * (case-insensitive substring match).
 *
 * Returns an empty Set when query is blank / whitespace-only.
 */
export function searchPeople(people: PersonDto[], query: string): Set<string> {
  const normalized = normalizeForSearch(query);
  if (!normalized) return new Set();

  const matches = new Set<string>();

  for (const person of people) {
    if (normalizeForSearch(getPersonDisplayName(person)).includes(normalized)) {
      matches.add(person.id);
    }
  }

  return matches;
}

/**
 * Returns the matching people in the order they appear in the tree.
 *
 * This is useful for navigation because the ordered list can be stepped through
 * while the Set-based search result remains useful for highlight lookups.
 */
export function searchPeopleInOrder(people: PersonDto[], query: string): PersonDto[] {
  const matches = searchPeople(people, query);
  if (matches.size === 0) {
    return [];
  }

  return people.filter((person) => matches.has(person.id));
}

/**
 * Returns a human-readable summary of the search result count.
 *
 * Examples:
 *   (0, 'Alice') → 'No matches for "Alice"'
 *   (1, 'Alice') → '1 match'
 *   (3, 'Ali')   → '3 matches'
 */
export function getSearchSummary(matchCount: number, query: string): string {
  if (matchCount === 0) return `No matches for "${query.trim()}"`;
  if (matchCount === 1) return '1 match';
  return `${matchCount} matches`;
}
